import { useContext, useEffect, useMemo, useRef } from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import { Box, CircularProgress } from "@mui/material";
import {
  AmountBox,
  AppAmountBoxWrapper,
  FilterNonIdeal,
} from "../../../common";
import { AppPagination } from "../../../../Libraries";
import { useAuthContext } from "../../../../context/AuthContext";
import ItemList from "./ItemList";
import AccountTableHeader from "./AccountTableHeader";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { useNavigate } from "react-router-dom";
import { buildFiltersConfig, formatToISOString } from "../../../../utils";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import AppTableFilters from "../../../common/AppTableFilters";
import {
  DEFAULT_DATE_FILTERS_EXPORT,
  ITEMS_PER_PAGE,
  TABLE_FILTER_CONTACT,
} from "../../../../constants";
import useDownlaodPdf from "../../../../hooks/useDownlaodPdf";
import NonIdealNew from "../../../common/NonIdealNew";

const AccountList = ({
  accountList,
  handleAccountList,
  totalCount,
  totalTake,
  totalGiven,
  setPageSize,
  pageSize,
  accountWrapperClassName,
  accountListBodyWarpperClassName,
  isLoading = false,
  filters,
  appliedChips,
  handleFilterChange,
  resetFilters,
  handleRemoveChip,
  isAppliedFilter,
  isAppliedFilterOtherThenKeywords,
  isResetFilter,
  setIsResetFilter,
}) => {

  const isFetchingRef = useRef(false);

  const { user, business } = useAuthContext();
  const { onDownload, downloadLoading } = useDownlaodPdf(user);
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();

  const isSuppliers = useMemo(() => {
    return filters.customers?.toLowerCase() == "suppliers";
  }, [filters?.customers]);

  const isByOthers = useMemo(() => {
    return filters.customers?.toLowerCase() == "by others";
  }, [filters?.customers]);

  const handleNewCall = async () => {
    isFetchingRef.current = true;
    setPageSize(0);
    await handleAccountList({
      business,
      filters: filters,
      offset: 0,
      limit: ITEMS_PER_PAGE,
    });
    isFetchingRef.current = false;
  };

  const searchDebounceRef = useRef(null);
  const prevFiltersRef = useRef(filters);

  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    // "By others" is the only tab whose keyword search hits the server (GET_SHARED_ACCOUNTS);
    // Customers/Suppliers search is a local WatermelonDB query, so only debounce that one case.
    const isKeywordsOnlyChange =
      prevFilters.keywords !== filters.keywords &&
      Object.keys(filters).every(
        (key) => key === "keywords" || filters[key] === prevFilters[key],
      );
    prevFiltersRef.current = filters;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    if (isByOthers && isKeywordsOnlyChange) {
      searchDebounceRef.current = setTimeout(() => {
        handleNewCall();
      }, 500);
    } else {
      handleNewCall();
    }

    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [filters]);

  const generateReport = async (startDate, endDate) => {
    if (navigator.onLine) {
      const paramsForReport = {
        name: isByOthers
          ? "communityStatement.pdf"
          : !isSuppliers
            ? "customerStatement.pdf"
            : "supplierStatement.pdf",
        template_name: "accountreport.hbs",
        business_id: business.id,
        start_date: startDate ? formatToISOString(startDate) : "",
        end_date: endDate
          ? formatToISOString(endDate)
          : startDate
            ? formatToISOString(startDate)
            : "",
        type: isByOthers ? "shared" : "customer",
        pdf: true,
        supplier: isSuppliers,
      };

      triggerLogEvent(
        analytics,
        isByOthers
          ? "acc_community_statement_download"
          : !isSuppliers
            ? "acc_customer_statement_download"
            : "acc_supplier_statement_download",
        {},
      );
      onDownload(paramsForReport, false, navigate);
    }
  };

  const handleGoDetail = (item) => {
    navigate(
      `/Accounts/AccountDetail/${item.id}${isByOthers ? `?type=community&contact=${item?.name}` : ""}`,
    );
  };

  const data = useMemo(() => {
    const result = [];

    result.push({
      amount: formatNum(Math.abs(Math.round(totalTake * 10) / 10)) || 0,
      title: I18n.i_will_give,
      id: "i_will_give",
      rsLabel: true,
      amountParaTextClassname: "foundationGreenColor",
    });

    result.push({
      amount: formatNum(Math.abs(Math.round(totalGiven * 10) / 10)) || 0,
      title: I18n.i_will_receive,
      id: "i_will_receive",
      rsLabel: true,
      amountParaTextClassname: "foundationOrangeColor",
    });

    return result;
  }, [totalGiven, totalTake, I18n]);

  const handleNavigateToCreateContact = (e) => {
    e.preventDefault();
    triggerLogEvent(analytics, "acc_add_contact", { source: "list" });
    navigate("/Accounts/ContactForm", {
      state: {
        isSupplier: isSuppliers,
        name : filters?.keywords || "",
      },
    });
  };

  const isShowCustomerNonIdeal =
    accountList.length == 0 &&
    filters?.customers == "Customers" &&
    !isLoading &&
    !isAppliedFilter;

  const isShowSupplierNonIdeal =
    accountList.length == 0 &&
    filters?.customers == "Suppliers" &&
    !isLoading &&
    !isAppliedFilter;

  const isShowByOthersNonIdeal =
    accountList.length == 0 &&
    filters?.customers == "By others" &&
    !isLoading &&
    !isAppliedFilter;

  return (
    <Box className={accountWrapperClassName || "AccountWrapper"}>
      <AppTableFilters
        filters={TABLE_FILTER_CONTACT}
        onFilterChange={handleFilterChange}
        appliedChips={appliedChips}
        filterState={filters}
        onRemoveChip={(chip) => {
          isFetchingRef.current = true;
          handleRemoveChip(chip);
        }}
        isResetFilter={isResetFilter}
        setIsResetFilter={setIsResetFilter}
        filtersConfig={buildFiltersConfig(TABLE_FILTER_CONTACT, [], null, null, null, null, null, null, null, I18n)}
        exportConfig={{
          onExport: generateReport,
          loading: downloadLoading,
          dateFilters: DEFAULT_DATE_FILTERS_EXPORT,
          applyBtnText: I18n.export,
          spinnerColor: "black",
        }}
      />

      <AppAmountBoxWrapper>
        <AmountBox data={data} />
      </AppAmountBoxWrapper>
      <AccountTableHeader />

      <Box
        className={`${accountListBodyWarpperClassName || "accountListBodyWarpper"} generic-scrollbar`}
        id="scrollableDiv"
      >
        {isLoading &&
        (accountList?.length === 0 || isFetchingRef.current) &&
        filters?.customers == "By others" ? (
          <Box
            className="d-flex justify-content-center align-items-center"
            style={{ height: "100%" }}
          >
            <CircularProgress size="30px" />
          </Box>
        ) : isShowCustomerNonIdeal ? (
          <NonIdealNew
            title={I18n.no_customers_yet}
            subtitle={I18n.you_have_not_added_any_customer}
            button1Title="add_user"
            onButton1Click={handleNavigateToCreateContact}
            loading={false}
            disabledOne={false}
            buttonOneId={"create_contact"}
          />
        ) : isShowSupplierNonIdeal ? (
          <NonIdealNew
            title={I18n.no_suppliers_yet}
            subtitle={I18n.add_suppliers_to_track_payments}
            button1Title="add_supplier"
            onButton1Click={handleNavigateToCreateContact}
            loading={false}
            disabledOne={false}
            buttonOneId={"create_contact"}
          />
        ) : isShowByOthersNonIdeal ? (
          <NonIdealNew
            title={I18n.no_transactions_for_others_yet}
            subtitle={
              I18n.when_someone_record_a_transaction_using_your_contact_details
            }
          />
        ) : accountList.length > 0 ? (
          <AppPagination
            data={{
              handleNext: (e) => {
                if (accountList.length < totalCount) {
                  setPageSize((value) => value + 1);
                  handleAccountList({
                    business,
                    is_supplier: isSuppliers,
                    filters,
                    offset: (pageSize + 1) * ITEMS_PER_PAGE,
                    limit: ITEMS_PER_PAGE,
                    isCalculate: false,
                  });
                }
              },
              pageLoad: true,
              count: totalCount,
              list: accountList,
              scrollableTarget: "scrollableDiv",
            }}
          >
            {accountList.map((item, i) => (
              <ItemList
                key={i}
                index={i}
                accountDetail={item}
                onSelect={() => handleGoDetail(item)}
                onEdit={() => navigate(`/Accounts/ContactForm/Edit/${item.id}`)}
                filters={filters}
                onDeleteSuccess={() => {
                  triggerLogEvent(analytics, "acc_edit_deleted", {
                    from: "acc_options_delete",
                  });
                  setPageSize(0);
                  handleAccountList({
                    business,
                    is_supplier: isSuppliers,
                    filters,
                    offset: 0,
                    limit: ITEMS_PER_PAGE,
                  });
                }}
              />
            ))}
          </AppPagination>
        ) : filters.keywords &&
          !isAppliedFilterOtherThenKeywords &&
          filters?.customers != "By others" ? (
          <FilterNonIdeal
            title={
              <>
                {I18n.we_could_not_find_match}
                <b>&apos;{filters.keywords}&apos;</b>
              </>
            }
            subTitle={
              <a
                href="/Accounts/ContactForm"
                onClick={handleNavigateToCreateContact}
              >
                {I18n.create_search_term_as_new_contact.replace(
                  "{name}",
                  filters.keywords,
                )}
              </a>
            }
          />
        ) : !isLoading ? (
          <Box className="h-100">
            <FilterNonIdeal
              title={I18n.no_transactions_matching_those_filters}
              subTitle={I18n.try_adjusting_filters}
              subTitleTextOnClick={() => {
                isFetchingRef.current = true;
                resetFilters();
              }}
              subTitleText={I18n.reset_small}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default AccountList;
